import { asColorLike } from 'ol/colorlike';
import {
  defaultFillStyle,
  defaultLineCap,
  defaultLineDash,
  defaultLineDashOffset,
  defaultLineJoin,
  defaultLineWidth,
  defaultMiterLimit,
  defaultStrokeStyle
} from 'ol/render/canvas';

import { logger } from 'cadenza/utils/logging';
import { Oval } from 'cadenza/map/geom/oval';
import { HIT_TOLERANCE } from 'cadenza/map/map-constants';
import {
  getParametersForDrawingTheOvalOnTheCanvasContext,
  rotateCanvasContextIfItemToDrawIsRotated,
  unrotateCanvasContextToDrawItemUnrotated
} from 'cadenza/map/geom/canvas-renderer-utilities';

import type { Stroke, Fill, Style } from 'ol/style';
import type { Coordinate } from 'ol/coordinate';
import type Feature from 'ol/Feature';
import type { RenderFunction } from 'ol/style/Style';

/**
 * Creates the render function for an oval
 *
 * @param style - OL Style class with the styles configured for the oval, like "fill" and "stroke"
 */
export function getOvalRenderer (style: Style): RenderFunction {
  return (coords, state, ...args) => {
    if (!(coords && coords.length === 2)) {
      logger.warn('For displaying an oval a "line string" consisting of 2 coordinates is expected. '
          + 'The 2 coordinates are calculated by OL using the flatCoordinates of the oval which have to be '
          + 'the following to get the implementation work correctly: '
          + '1.) The center coordinate and 2.) The "top right" corner coordinate', coords);
      return;
    }
    const [ center, corner ] = coords as [center: Coordinate, corner: Coordinate];
    const { context, feature } =
        state as { context: CanvasRenderingContext2D; feature: Feature };
    const geometry = feature.getGeometry();
    if (!(geometry instanceof Oval)) {
      logger.error('trying to draw other geometry with oval renderer', coords, state, args);
      return;
    }
    const ovalStyle = style || feature.getStyle();
    if (!ovalStyle) {
      logger.error('missing style in oval renderer', args);
      return;
    }

    const fillStyle = ovalStyle.getFill();
    const strokeStyle = ovalStyle.getStroke();
    if (fillStyle === undefined && strokeStyle === undefined) {
      logger.error('missing fill/stroke style in oval renderer', coords, state, args);
      return;
    }

    const clockwiseRotationInDegree0To359 = geometry.getClockwiseRotationInDegree0To359();

    const {
      x,
      y,
      radiusX,
      radiusY,
      rotation,
      startAngle,
      endAngle
    } = getParametersForDrawingTheOvalOnTheCanvasContext(center, corner);

    // Draw the oval now on the CanvasRenderingContext2D. For doing this:
    // 1.) Save the current state of the context to the stack
    context.save();
    // 2.) Reverse rotate the context (if needed) to be able to draw a rotated oval with the same functionality as an unrotated oval
    if (clockwiseRotationInDegree0To359 > 0) {
      unrotateCanvasContextToDrawItemUnrotated(context, center, clockwiseRotationInDegree0To359);
    }
    // 3. Draw the oval with a new path in an unrotated state, apply the styling to the oval drawn and close the path afterwards
    context.beginPath();
    context.ellipse(x, y, radiusX, radiusY, rotation, startAngle, endAngle);
    fill(context, fillStyle);
    stroke(context, strokeStyle);
    context.closePath();
    // 4. Rotate the context to the initial state (if needed) after drawing the oval
    if (clockwiseRotationInDegree0To359 > 0) {
      rotateCanvasContextIfItemToDrawIsRotated(context, center, clockwiseRotationInDegree0To359);
    }
    // 5.) Restore the previous state of the context again
    context.restore();
  };
}

function getFillStyleColor (context: CanvasRenderingContext2D, fillStyle: Fill) {
  const hitDetectionContextSize = Math.round(HIT_TOLERANCE) * 2 + 1; // Taken from CanvasReplayGroup.prototype.forEachFeatureAtCoordinate
  const isRenderingForHitDetection = context.canvas.width === hitDetectionContextSize
      && context.canvas.height === hitDetectionContextSize;
  if (isRenderingForHitDetection) {
    // OpenLayers does use the result of a custom renderer (this) for hit detection of the drawn
    // object (oval here) as well.
    // It basically uses the renderer to draw the oval on a small canvas context which has the size
    // of the hit tolerance and which is centered at cursor position.
    // To also be able to select an oval by clicking on their fully transparent area we need to draw
    // an image without alpha in case of hit detection. Otherwise, if alpha is 0, the occupied area
    // is not filled and thus not handled as hit area of the oval.
    return 'rgba(0,0,0,1)';
  } else {
    return asColorLike(fillStyle.getColor() || defaultFillStyle);
  }
}

// inspired by CanvasReplay.prototype.setFillStrokeStyle
function fill (context: CanvasRenderingContext2D, fillStyle: Fill | null) {
  if (!fillStyle) {
    return;
  }
  context.fillStyle = getFillStyleColor(context, fillStyle);
  context.fill();
}

// inspired by CanvasReplay.prototype.setFillStrokeStyle
function stroke (context: CanvasRenderingContext2D, strokeStyle: Stroke | null) {
  if (!strokeStyle) {
    return;
  }
  // this is fix for a bug in macOS where devicePixelRatio is different that in other os
  const pixelRatioMultiplier = window.devicePixelRatio;
  context.strokeStyle = asColorLike(strokeStyle.getColor() || defaultStrokeStyle);
  context.lineCap = strokeStyle.getLineCap() !== undefined ? strokeStyle.getLineCap()! : defaultLineCap;
  context.lineJoin = strokeStyle.getLineJoin() !== undefined ? strokeStyle.getLineJoin()! : defaultLineJoin;
  context.lineWidth = strokeStyle.getWidth() !== undefined
    ? strokeStyle.getWidth()! * pixelRatioMultiplier : defaultLineWidth * pixelRatioMultiplier;
  context.miterLimit = strokeStyle.getMiterLimit() !== undefined
    ? strokeStyle.getMiterLimit()! * pixelRatioMultiplier : defaultMiterLimit * pixelRatioMultiplier;
  if (context.setLineDash) {
    context.lineDashOffset = strokeStyle.getLineDashOffset() !== undefined
      ? strokeStyle.getLineDashOffset()! * pixelRatioMultiplier : defaultLineDashOffset * pixelRatioMultiplier;
    context.setLineDash(strokeStyle.getLineDash()
      ? strokeStyle.getLineDash()!.map(size => size * pixelRatioMultiplier)
      : defaultLineDash.map(size => size * pixelRatioMultiplier));
  }
  context.stroke();
}
